import {
  getPipeSeries,
  getProjectsProjectidSelectionsProjectselectionid,
  GetProjectsProjectidSelectionsProjectselectionidStatus200,
  getUsersPreferencesTechnicalSettings,
} from "@imi/api-client/index.js";
import { buildPipeSeriesPreference } from "@src/core/api/pipeSeriesPreference";
import { productRepository } from "@src/core/engine/products/productRepository";
import { projectRepository } from "@src/core/engine/projects/projectRepository";
import { isMainProduct } from "@src/core/products/isMainProduct";
import { assert, pick } from "radashi";
import { LoaderFunctionArgs } from "react-router";

export async function selectionEditPageLoader({ params }: LoaderFunctionArgs) {
  const { projectId, selectionId } = params;
  assert(projectId, "projectId is required");
  assert(selectionId, "selectionId is required");

  const selectionDetails =
    await getProjectsProjectidSelectionsProjectselectionid({
      path: { projectId, projectSelectionId: selectionId },
    });

  if (selectionDetails.status !== 200) {
    throw new Error("Failed to fetch selection details");
  }

  return {
    selection: {
      ...selectionDetails.data,
      detailedProducts: selectionDetails.data.products.map((product) =>
        productRepository.getById({
          id: product.productId,
          productSetId: product.productSetId,
          type: product.productType,
        }),
      ),
    },
    ...(await calculateOutputs(selectionDetails.data)),
  };
}

async function calculateOutputs(
  selection: GetProjectsProjectidSelectionsProjectselectionidStatus200,
) {
  if (selection.type === "direct") return {};

  const [customPipeSeries, technicalSettings] = await Promise.all([
    getPipeSeries(),
    getUsersPreferencesTechnicalSettings(),
  ]);

  const mainProduct = selection.products.find(({ productType }) =>
    isMainProduct(productType),
  );
  assert(mainProduct, "No main product found in the selection");

  if (selection.type === "technical") {
    const { pipeResults, suggestion } =
      projectRepository.calculateSelectionOutput(
        mainProduct.productId,
        selection.outputPipeSize,
        {
          categoryId: selection.categoryId,
          customPipeSeries: customPipeSeries.data,
          filters: selection.filters,
          pipeSeriesPreference: buildPipeSeriesPreference(
            selection.pipeSeriesPreference,
          ),
          technicalSettings: technicalSettings.data,
          waterCharacteristics: selection.waterCharacteristics,
        },
      );
    return { pipeResult: pipeResults, suggestion };
  }

  if (selection.type === "batch") {
    const inputs = pick(selection, ["dp", "flow", "pipeSize"]);
    const { pipeResults, suggestion } =
      projectRepository.calculateSelectionOutput(
        mainProduct.productId,
        selection.outputPipeSize,
        {
          categoryId: selection.filters.categoryId,
          customPipeSeries: customPipeSeries.data,
          filters: {
            ...selection.filters,
            ...inputs,
          },
          pipeSeriesPreference: buildPipeSeriesPreference(
            selection.filters.pipeSeriesPreference,
          ),
          technicalSettings: technicalSettings.data,
          waterCharacteristics: selection.filters.waterCharacteristics,
        },
      );
    return { pipeResult: pipeResults, suggestion };
  }
  return {};
}
